import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import PageLinkBar from '@ui-parts/PageLinkBar';
import ProgressTracker from '@ui-parts/ProgressTracker';
import SurveyStatusDetailBox from '@ui-parts/SurveyStatusDetailBox';
import SurveyBasicInfoBox from '@ui-parts/SurveyBasicInfoBox';
import SurveyPriceBox from '@ui-parts/SurveyPriceBox';
import ModalWithInput from '@ui-parts/ModalWithInput';
import SaveButton from '@ui-elements/SaveButton';

import { Typography } from '@mui/material';

import { calcEnqueteCollectSum } from '@functional';
import {
  surveyTitleValidationRule,
  surveyUrlValidationRule,
  numberOfQuestionsValidationRule,
} from '~/constants/validationRules';

import {
  PROGRESS_TRACKER_LABELS,
  PROGRESS_TYPE,
  SURVEY_PROGRESSES,
  SURVEY_STATUSES,
  TEXT_MAX_LENGTH,
} from '~/constants';
import { ROUTES } from '~/constants/routes';
import { callThunk, updateSurvey, updatedNumberOfQuestions } from '~/api';
import { FONT_SIZES } from '~/assets/styles/base';
import { SURVEY_FORM_TYPE } from '~/common/constants';

const HeaderBox = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StatusDetailBoxArea = styled.div`
  margin-bottom: 20px;
`;

const LeftItem = styled.div`
  padding-right: 10px;
  width: 60%;
`;

const RightItem = styled.div`
  padding-left: 10px;
  width: 40%;
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
  width: 100%;
  text-align: left;
  font-size: ${FONT_SIZES.title1};
  margin-bottom: 20px;
  gap: 10px;
`;

const Title = styled(Typography)`
  font-size: ${FONT_SIZES.title1};
`;

const Category = styled(Typography)`
  font-size: ${FONT_SIZES.title2};
  margin-right: 20px;
`;

const initialModalOpen = {
  surveyTitle: false,
  surveyUrl: false,
  surveyPrice: false,
};

function Header({ survey, canEdit = false }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const collectCounts = useSelector((state) => state.quotas.collectCounts);

  const isCompleted = useMemo(
    () => survey.surveyStatusId === SURVEY_STATUSES.COMPLETED,
    [survey.surveyStatusId]
  );

  const enqueteCollectCountsTotal = calcEnqueteCollectSum(collectCounts);

  // local states
  const [isModalOpen, setIsModalOpen] = useState(initialModalOpen);

  const links = [
    {
      label: t('appCommon.enqueteList'),
      routePath: ROUTES.USER.MYPAGE,
    },
    {
      label: `${t('myPage.surveyId')} ${survey?.id} ${survey?.name}`,
      routePath: null, // 最後の項目はリンクではありません
    },
  ];

  const titleForm = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: useMemo(() => {
      return {
        surveyTitle: survey.name,
      };
    }, [survey]),
  });

  const urlForm = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: useMemo(() => {
      return {
        surveyUrl: survey.url,
      };
    }, [survey]),
  });

  // 設問数用のuseForm(バリデーション)
  const numberOfQuestionsForm = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: useMemo(() => {
      return {
        numberOfQuestions: survey.numberOfQuestions,
      };
    }, [survey]),
  });

  // functions
  const closeModals = () => {
    setIsModalOpen(initialModalOpen);
  };

  const openModal = (name) => {
    setIsModalOpen({
      ...isModalOpen,
      [name]: true,
    });
  };

  const handleClickSaveSurveyTitle = () => {
    dispatch(
      callThunk({
        action: updateSurvey,
        payload: { name: titleForm.getValues('surveyTitle') },
      })
    );
    closeModals();
  };

  const handleClickSaveSurveyUrl = () => {
    dispatch(
      callThunk({
        action: updateSurvey,
        payload: { url: urlForm.getValues('surveyUrl').replace(/\r\n|\r|\n/g, '') },
      })
    );
    closeModals();
  };

  const handleClickSaveNumOfQuestion = () => {
    const numberOfQuestions = numberOfQuestionsForm.getValues('numberOfQuestions');
    dispatch(
      callThunk({
        action: updatedNumberOfQuestions,
        payload: { numberOfQuestions, targetSamplesTotal: survey.targetSamplesTotal },
      })
    );
    closeModals();
  };

  useEffect(() => {
    titleForm.setValue('surveyTitle', survey.name);
    urlForm.setValue('surveyUrl', survey.url);
    numberOfQuestionsForm.setValue('numberOfQuestions', survey.numberOfQuestions);
  }, [survey]);

  const getDataSet = () => {
    const dataSetList = [];
    let surveyTitleData = {};
    let surveyUrlData = {};

    if (survey.type === SURVEY_FORM_TYPE.GOOGLE_FORM) {
      surveyTitleData = {
        name: 'surveyTitle',
        title: t('survey.SurveyBasicInfoBox.surveyTitle'),
        label: survey.name,
        modal: {
          open: isModalOpen.surveyTitle,
          titleProps: {
            title: t('survey.SurveyBasicInfoBox.surveyTitleModal.title'),
            desc: t('survey.SurveyBasicInfoBox.surveyTitleModal.desc'),
            warnText: t('appCommon.forbiddenText'),
            warnTop: '10px',
            warnRight: '10px',
            warnLink: ROUTES.COMMON.FORBIDDEN_TEXT,
          },
          inputs: [
            {
              rules: surveyTitleValidationRule,
              nameProp: 'surveyTitle',
              placeholder: t('survey.SurveyBasicInfoBox.surveyTitleModal.placeholder'),
              width: '100%',
              position: 'center',
              errorZIndex: 10000,
              dataTestId: 'SurveyTitleInput',
              handleChange: (e) => {
                titleForm.setValue('surveyTitle', e.target.value);
              },
              handleBlur: (e) => {
                titleForm.setValue('surveyTitle', e.target.value.trim());
              },
              inputProps: { maxLength: 70 },
            },
          ],
          control: titleForm.control,
          handleClickSave: titleForm.handleSubmit(handleClickSaveSurveyTitle),
          closeModal: () => {
            titleForm.setValue('surveyTitle', survey.name);
            titleForm.clearErrors();
            closeModals();
          },
          openModal,
          dataTestId: 'SurveyTitleModal',
        },
      };
      surveyUrlData = {
        name: 'surveyUrl',
        title: t('survey.SurveyBasicInfoBox.surveyUrlGoogleForm'),
        label: survey.url,
        modal: {
          open: isModalOpen.surveyUrl,
          titleProps: {
            title: t('survey.SurveyBasicInfoBox.surveyUrlModal.title'),
            dangerouslySetDesc: t('survey.SurveyBasicInfoBox.surveyUrlModal.desc'),
          },
          inputs: [
            {
              rules: surveyUrlValidationRule,
              nameProp: 'surveyUrl',
              placeholder: t('survey.SurveyBasicInfoBox.surveyUrlModal.placeholder'),
              width: '100%',
              position: 'center',
              errorZIndex: 10000,
              dataTestId: 'SurveyUrlInput',
              handleChange: (e) => {
                urlForm.setValue('surveyUrl', e.target.value.trim());
              },
              handleBlur: (e) => {
                urlForm.setValue('surveyUrl', e.target.value.trim());
              },
              multiline: true,
              rows: 2,
              inputProps: { maxLength: TEXT_MAX_LENGTH },
            },
          ],
          control: urlForm.control,
          handleClickSave: urlForm.handleSubmit(handleClickSaveSurveyUrl),
          closeModal: () => {
            urlForm.setValue('surveyUrl', survey.url);
            urlForm.clearErrors();
            closeModals();
          },
          openModal,
          dataTestId: 'SurveyUrlModal',
        },
      };
    } else if (survey.type === SURVEY_FORM_TYPE.ORIGINAL_FORM) {
      surveyTitleData = {
        name: 'surveyTitle',
        title: t('survey.SurveyBasicInfoBox.surveyTitle'),
        label: survey.name,
      };
      surveyUrlData = {
        name: 'surveyUrl',
        title: t('survey.SurveyBasicInfoBox.surveyUrlOriginalForm'),
        isPreview: true,
      };
    }

    dataSetList.push(surveyTitleData);
    dataSetList.push(surveyUrlData);
    return dataSetList;
  };

  return (
    <>
      {links !== null && <PageLinkBar links={links} />}

      {canEdit && (
        <ProgressTracker
          width="618px"
          labels={PROGRESS_TRACKER_LABELS[PROGRESS_TYPE.SURVEY].map((label) => t(label))}
          currentNum={SURVEY_PROGRESSES[ROUTES.USER.SURVEY]}
        />
      )}

      {survey.surveyStatusId === SURVEY_STATUSES.REGISTERING ? (
        <TitleWrapper data-testid="pages-UserHeader-pagetitle">
          <Category>{t('pageTitles.user.survey.category')}</Category>
          <Title>{t('pageTitles.user.survey.title')}</Title>
        </TitleWrapper>
      ) : (
        <StatusDetailBoxArea>
          {survey.surveyStatusId && <SurveyStatusDetailBox survey={survey} />}
        </StatusDetailBoxArea>
      )}

      <HeaderBox>
        <LeftItem>
          <SurveyBasicInfoBox
            dataset={getDataSet()}
            canEdit={canEdit}
            surveyType={survey.type}
            surveyId={survey.id}
          />
        </LeftItem>
        <RightItem>
          <SurveyPriceBox
            isCompleted={isCompleted}
            openModal={() => openModal('surveyPrice')}
            numberOfQuestions={survey.numberOfQuestions}
            targetSamplesTotal={survey.targetSamplesTotal}
            enqueteCollectCountsTotal={Number(enqueteCollectCountsTotal)}
            canEdit={survey.type === SURVEY_FORM_TYPE.GOOGLE_FORM ? canEdit : false}
          />
        </RightItem>

        {/* 設問数編集モーダル */}
        <ModalWithInput
          open={isModalOpen.surveyPrice}
          handleClose={() => {
            numberOfQuestionsForm.setValue('numberOfQuestions', survey.numberOfQuestions);
            numberOfQuestionsForm.clearErrors();
            closeModals();
          }}
          control={numberOfQuestionsForm.control}
          modalWidth="600px"
          titleProps={{
            title: t('quotas.surveyPriceBox.editNumberOfQuestions'),
            desc: t('quotas.surveyPriceBox.cationNumberOfQuestions'),
            descVariant: 'bodyDefault',
            warnText: t('appCommon.countQuestionNum'),
            warnTop: '10px',
            warnRight: '10px',
            warnLink: ROUTES.COMMON.GGL_QUESTION_NUM,
          }}
          inputs={[
            {
              rules: { ...numberOfQuestionsValidationRule },
              nameProp: 'numberOfQuestions',
              width: '100px',
              position: 'center',
              textAlign: 'right',
              inputFontSize: FONT_SIZES.body1,
              dataTestId: 'uiParts-SurveyPriceBox-modal-input',
              errorWidth: 'max-content',
              errorZIndex: 10000,
              onlyNum: true,
              inputProps: { maxLength: 11 },
            },
          ]}
          buttons={[
            <SaveButton
              key="save-button"
              handleClick={numberOfQuestionsForm.handleSubmit(handleClickSaveNumOfQuestion)}
              sx={{ width: 148 }}
            />,
          ]}
          dataTestId="NumberOfQuestionsModal"
        />
      </HeaderBox>
    </>
  );
}

Header.propTypes = {
  survey: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    url: PropTypes.string,
    numberOfQuestions: PropTypes.number,
    targetSamplesTotal: PropTypes.number,
    surveyStatusId: PropTypes.number,
    type: PropTypes.number,
  }).isRequired,
  canEdit: PropTypes.bool,
};

export default Header;
